import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getLoggedinUser } from "../../helpers/api_helper";

/**
 * Per-organization behaviour flags, delivered on the login/refresh payload by
 * backend/core/serializers.py OrganizationSerializer.
 *
 * Mirrors BaseOrgConfig in backend/core/org_config.py. Defaulting to BUSINESS is
 * deliberate: a cached payload predating this deploy has no `config`, and showing
 * an over-featured company UI to a personal org is harmless, whereas hiding DDV
 * from a real company is not. The backend enforces every flag regardless, so a
 * stale client cannot write taxed data into a personal org.
 */
export const DEFAULT_ORG_CONFIG = {
  mode: "BUSINESS",
  tax_enabled: true,
  single_date: false,
  track_issuance: true,
  intents_column_order: [],
};

// Plain getter for module scope (route tables, sidebar builders) where a hook
// cannot be called. The value is static for a session, so a synchronous read is
// correct and needs no state.
export const getOrgConfig = () => ({
  ...DEFAULT_ORG_CONFIG,
  ...(getLoggedinUser()?.organization?.config || {}),
});

/**
 * Config for components. Prefers the store (loaded fresh on every app start by
 * loadOrgConfig), falling back to the cached login payload for the first render
 * before that fetch lands. Reading through the store is what makes a component
 * re-render once the fresh config arrives.
 */
export const useOrgConfig = () => {
  const stored = useSelector((state) => state.OrgConfig?.config);
  return useMemo(
    () => ({
      ...DEFAULT_ORG_CONFIG,
      ...(stored || getLoggedinUser()?.organization?.config || {}),
    }),
    [stored]
  );
};

// Which amount the insights pages chart by default. Without VAT the net and gross
// columns hold the same number, so personal orgs use the plain gross field and the
// selector is hidden entirely.
export const defaultAmountField = (cfg) =>
  cfg.tax_enabled ? "amount_no_tax" : "amount";
